// ═══════════════════════════════════════════════════════════════════════════════
// Pairing code expiry helpers (viewer format YYYY-MM-DD HH:mm)
// ═══════════════════════════════════════════════════════════════════════════════

export type PairingExpiryDurationUnit = 'minutes' | 'hours' | 'day' | 'days';

export type PairingExpiryDurationParts = {
  count: string;
  unit: PairingExpiryDurationUnit;
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

const formatDurationCount = (value: number): string => {
  if (Number.isInteger(value)) {
    return String(value);
  }
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return String(rounded);
};

/** Pick display unit: minutes (<60), hours (>=60), day/days (>=24h). */
export const getPairingExpiryDurationParts = (
  expiryMinutes: number
): PairingExpiryDurationParts => {
  if (expiryMinutes >= MINUTES_PER_DAY) {
    const count = formatDurationCount(expiryMinutes / MINUTES_PER_DAY);
    return {
      count,
      unit: count === '1' ? 'day' : 'days',
    };
  }
  if (expiryMinutes >= MINUTES_PER_HOUR) {
    return {
      count: formatDurationCount(expiryMinutes / MINUTES_PER_HOUR),
      unit: 'hours',
    };
  }
  return {
    count: formatDurationCount(expiryMinutes),
    unit: 'minutes',
  };
};

const DURATION_I18N_KEY: Record<PairingExpiryDurationUnit, string> = {
  minutes: 'pairing.generate.durationMinutes',
  hours: 'pairing.generate.durationHours',
  day: 'pairing.generate.durationDay',
  days: 'pairing.generate.durationDays',
};

export const formatPairingExpiryDurationLabel = (
  expiryMinutes: number,
  translate: (key: string, params?: Record<string, string | number>) => string
): string => {
  const parts = getPairingExpiryDurationParts(expiryMinutes);
  return translate(DURATION_I18N_KEY[parts.unit], { count: parts.count });
};

export const isPairingCodeNotExpired = (expiresAt: string | null | undefined): boolean => {
  const normalized = expiresAt?.trim() ?? '';
  if (!normalized) {
    return false;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(normalized);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const expiryMs = new Date(year, month - 1, day, hour, minute, 0, 0).getTime();
  return expiryMs > Date.now();
};
